import { ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components';
import { Button, FormHelperText, Tooltip } from '@mui/material';
import { TMeltingLot } from '../../Models/Inbound';
import Title from '../custom/Title';
import DynamicForm, { TDynamicFieldProps, TField, TFieldType, getFormConfigDeepCopy, useDynamicForm } from '../forms/DynamicFormNew';
import { TransactionContext } from '../../contexts/TransactionContext';
import { NotificationContext } from '../../contexts/NotificationContext';
import useService from '../../hooks/useService';
import { createOrUpdateLotDetails, extractXRFReport } from '../../services/Transactions';
import { TCreateOrUpdateMeltingLotParams } from '../../services/Transactions/types';
import { FLOAT_REGEX, LINE_PRODUCT_TYPE, METAL_TYPES, MMTC_PRODUCT_NONMELTED_LIMIT, NUMBER_REGEX, TRANSACTION_STATUS, TRANSACTION_TYPES } from '../../utils/constant';
import { FileUpload, Info } from '@mui/icons-material';
import { fileToText, getUserPrivilages } from '../../utils/helpers';
import { CustomerContext } from '../../contexts/CustomerContext';
import PropertiesViewer from '../custom/PropertiesViewer';


const Divider = styled.div`
    border: 1px solid #EBEBEC;
`

const XRFInput: ({ disabled, children }: { disabled?: Boolean, children: React.ReactNode } & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => React.JSX.Element = styled.div`
    width: 100%;
    height: 40px;
    padding: 10px 18px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    border:${props => props.disabled ? '1px solid silver' : '1px solid var(--text_color_2)'};
    cursor:${props => props.disabled ? 'default' : 'pointer'};
    border-radius: 4px;
`

const XRFLabel: (props: React.HTMLAttributes<HTMLDivElement> & { isFilled: boolean }) => React.JSX.Element = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    font-size: 1rem;
    transform: translate(14px, 7px) scale(1);
    transform-origin: left;
    transition: 0.1s;
    background: white;
    padding: 0 5px;

    ${(props) => props.isFilled ? `
        transform: translate(14px, -12px) scale(0.75);
    `: ``}
`

const MELTING_LOT_DETAILS_FIELDS = [
    {
        id: "metal",
        label: 'Metal',
    },
    {
        id: "primaryMetal",
        label: 'Primary Metal',
    },
    {
        id: "declaredFineness",
        label: 'Declared Fineness',
    },
    {
        id: "settlementFineness",
        label: 'Settlement Fineness'
    },
]

const UPDATE_MELTING_LOT_CONFIG: TField<TMeltingLot>[] = [
    {
        fieldName: 'id',
        isVisible: true,
        fieldType: 'text',
        label: 'Lot Number',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'declaredWeight',
        isVisible: true,
        fieldType: 'number',
        label: 'Declared Weight (GR)',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'netWeight',
        isVisible: true,
        fieldType: 'number',
        label: 'Net Weight(GR)',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'customerTakenWeight',
        isVisible: true,
        fieldType: 'text',
        label: 'Customer Taken Weight (GR)',
        width: 'calc(33% - 20px)',
        disabled: false,
        regexFilter: FLOAT_REGEX,
        decimalPlaces:3,
        // validation: {
        //     required: true
        // }
    },
    {
        fieldName: 'weightAfterMelting',
        isVisible: true,
        fieldType: 'text',
        label: 'Weight After Melting (GR)',
        width: 'calc(33% - 20px)',
        regexFilter: FLOAT_REGEX,
        decimalPlaces:3,
        validation: {
            required: true
        }
    },
    {
        fieldName: 'wamt',
        isVisible: true,
        fieldType: 'text',
        label: 'WAMT(GR)',
        width: 'calc(33% - 20px)',
        regexFilter: FLOAT_REGEX,
        decimalPlaces:3,
        // validation: {
        //     required: true
        // }
    },
    {
        fieldName: 'fineWeightToReturn',
        isVisible: true,
        fieldType: 'number',
        label: 'Fine Weight To Return (GR)',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'furnace',
        isVisible: true,
        fieldType: 'number',
        label: 'Furnace',
        width: 'calc(33% - 20px)',
        validation: {
            required: true
        }
    },
    {
        fieldName: 'metalReturnPercentage',
        isVisible: true,
        fieldType: 'number',
        label: 'Metal Return Percentage',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'fineness',
        isVisible: true,
        fieldType: 'custom',
        label: 'Fineness',
        width: 'calc(33% - 20px)',
        // validation: {
        //     required: true
        // }
    },
    {
        fieldName: 'fineWeight',
        isVisible: true,
        fieldType: 'number',
        label: 'Fine Weight (GR)',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'settlementFineness',
        isVisible: true,
        fieldType: 'number',
        label: 'Settlement Fineness',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'settlementNetWeight',
        isVisible: true,
        fieldType: 'number',
        label: 'Settlement Net Weight(GR)',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'finalSettlementNetWeight',
        isVisible: true,
        fieldType: 'number',
        label: 'Final Settlement Net Weight(GR)',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'meltingLoss',
        isVisible: true,
        fieldType: 'number',
        label: 'Melting Loss (GR)',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'buffingDustNetWeight',
        isVisible: true,
        fieldType: 'number',
        label: 'Buffing Dust Net Weight (GR)',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'buffingDustFine',
        isVisible: true,
        fieldType: 'number',
        label: 'Buffing Dust Fine (GR)',
        width: 'calc(33% - 20px)',
        disabled: true
    },
    {
        fieldName: 'metalType',
        fieldType: 'select',
        isVisible: false,
        label: 'Metal Type',
        width: 'calc(33% - 20px)',
        disabled: true,
        options: [
            { label: 'Gold', value: METAL_TYPES.GOLD },
            { label: 'Silver', value: METAL_TYPES.SILVER },
        ]
    }
]

type XRFElements = 'Au' | 'Ag' | 'Cu' | 'Zn' | 'Cd' | 'Ir' | 'Ru' | 'Os' | 'Pt'
const XRF_REPORT: { key: XRFElements, value: string }[] = [
    { key: 'Au', value: 'Gold' },
    { key: 'Ag', value: 'Silver' },
    { key: 'Cu', value: 'Copper' },
    { key: 'Zn', value: 'Zinc' },
    { key: 'Cd', value: 'Cadmium' },
    { key: 'Ir', value: 'Iridium' },
    { key: 'Ru', value: 'Ruthenium' },
    { key: 'Os', value: 'Osmium' },
    { key: 'Pt', value: 'Platinum' },
]

const XRFField = (props: TDynamicFieldProps<TMeltingLot>) => {
    const { config, error, formData, onChange } = props
    const { fieldName, label, disabled } = config
    const [visibleValue, setVisibleValue] = useState<number>(0)
    const [showReport, setShowReport] = useState(false)
    const [xrfReport, setXrfReport] = useState<{ [key: string]: any }>({})
    const { execute } = useService()
    const { transactionInfo: { globalTransactionNumber, transactionType }, meltingLot } = useContext(TransactionContext)

    const uploadXhrChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return

        const file = e.target.files[0]
        const fileText = await fileToText(file)
        const { data } = await execute(() => extractXRFReport({
            xrf: fileText,
            lotNumber: formData.id!,
            globalTransactionNumber: globalTransactionNumber!,
            transactionType
        }))

        if (data !== null) {
            setVisibleValue(data.fineness)
            onChange(data.finenessId)
        }
        e.target.value = ''
    }

    const clickHandler = () => {
        (document.querySelector('#xrfUploadInput') as HTMLInputElement).click();
    }

    const getValue = () => visibleValue ? visibleValue : formData[fieldName] as number
    const getMeltingLot = useCallback(() => {
        return meltingLot.filter(lot => lot.id === formData.id)[0]
    }, [meltingLot])

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <XRFInput onClick={() => !disabled && clickHandler()} disabled={disabled} >
                <XRFLabel isFilled={Boolean(getValue())}>{label}</XRFLabel>
                {getValue() ? <span>{getValue()}</span> : <></>}
            </XRFInput>
            <Button
                disabled={disabled}
                variant='contained'
                onClick={clickHandler} >
                <FileUpload />
            </Button>
            {getMeltingLot() && Object.keys(getMeltingLot().xrfReport ?? {}).length ?
                <>
                    <Tooltip title="XRF Report">
                        <div style={{ cursor: 'pointer' }} onClick={() => setShowReport(true)}><Info color='action' /></div>
                    </Tooltip>
                    <PropertiesViewer
                        show={showReport}
                        title='XRF Report'
                        onClose={() => setShowReport(false)}
                        properties={XRF_REPORT.map(row => ({ id: row.key, label: `${row.value} [${row.key}]`, value: getMeltingLot().xrfReport![row.key] }))}
                    />
                </>
                : <></>}
            <input type='file' style={{ display: 'none' }} id='xrfUploadInput' onChange={(e) => uploadXhrChange(e as any)} />
        </div>
        {error ? <FormHelperText style={{ marginLeft: '10px' }} error>{error || 'XRF is required'}</FormHelperText> : <></>}
    </div>
}

function MeltingLot({ lotNumber, lineIds, closeMeltingLot, isLotCompleted }: { lotNumber?: string, lineIds: string[], closeMeltingLot: () => any, isLotCompleted: boolean }) {
    const { execute, store } = useService()
    const { authStore, transactionsStore, notificationStore, customerStore } = store
    const { meltingLot, lineDetails: lineItems, transactionInfo: { globalTransactionNumber, status, transactionType }, inboundDetails } = transactionsStore
    const { notify } = notificationStore
    const { customerConfig } = customerStore
    const { user } = authStore
    const [initialRender, setInitialRender] = useState(true)

    const [manualFinnes, setManualFinness] = useState(false)
    const [manualInput, setManualInput] = useState(false)

    const privilages = getUserPrivilages(user)

    const { registerForm, formData, setFormData, setFormConfig, setField } = useDynamicForm({
        formConfig: getFormConfigDeepCopy(UPDATE_MELTING_LOT_CONFIG)
    })

    const doesLotContainAssuredProducts = () => {
        return Boolean(lineItems.filter(line => line.lotId === lotNumber && line.productType === LINE_PRODUCT_TYPE['MMTC-PAMP Product']).length)
    }

    const buybackAssuredCase = () => doesLotContainAssuredProducts() && transactionType === TRANSACTION_TYPES.BUY_BACK

    const submitHandler = async (newLot: TMeltingLot) => {
        let prevData = meltingLot.filter(lot=> lotNumber === lot.id)[0]
        if (lotNumber && Number(newLot.weightAfterMelting <= 0)) return notify('Weight After Melting cannot be 0','error')
        if (lotNumber && Number(newLot.weightAfterMelting) > Number(newLot.netWeight)) return notify('Weight After Melting cannot be greater than Net Weight', 'error')
        if (lotNumber && Number(newLot.wamt) > Number(newLot.weightAfterMelting)) return notify('WAMT cannot be greater than WAM', 'error')
        if (lotNumber && !prevData.settlementNetWeight && Number(newLot.wamt) > Number(newLot.weightAfterMelting) - Number(newLot.customerTakenWeight)) return notify('WAMT cannot be greater than Weight After Melting - Customer Taken Weight', 'error')

        const isNonMeltingProduct = Boolean(lineItems.filter(line => lineIds.includes(line.id!) && line.netWeight <= 10 && line.productType === LINE_PRODUCT_TYPE['MMTC-PAMP Product']).length)
        if ((transactionType != TRANSACTION_TYPES.BUY_BACK || !isNonMeltingProduct) && lotNumber && newLot.furnace < 1) {
            return notify('Number of Furnace cannot be less than 1', 'error')
        }

        // removing condition
        // const remainingWeight = getThreePointNumber(getThreePointNumber(inboundDetails.shipmentWeight) - getThreePointNumber(meltingLot.reduce((lotsWeight, lot) => {
        //     return lot.id !== lotNumber ? lotsWeight + Number(lot.netWeight) : Number(lotsWeight)
        // }, 0)))

        // if (remainingWeight < Number(newLot.netWeight)) return notify(`You cannot add lines of Net Weight more than ${remainingWeight}gm`, 'error')

        const params: TCreateOrUpdateMeltingLotParams = {
            lineIds: lineIds,
            globalTransactionNumber: globalTransactionNumber!,
            transactionType,
            metalType: newLot.metalType!
        }
        if (lotNumber) {

            if(Number(newLot.wamt) && !newLot.fineness) return notify('XRF is required','error')
            if(!Number(newLot.wamt) && newLot.fineness) return notify('WAMT is required','error')
            const lotDetails: TMeltingLot = {
                buffingDustFine: Number(newLot.buffingDustFine),
                customerTakenWeight: Number(newLot.customerTakenWeight),
                buffingDustNetWeight: Number(newLot.buffingDustNetWeight),
                declaredWeight: Number(newLot.declaredWeight),
                settlementNetWeight: Number(newLot.settlementNetWeight),
                finalSettlementNetWeight: Number(newLot.finalSettlementNetWeight),
                fineness: manualFinnes ? Number(newLot.fineness) : newLot.fineness,
                fineWeight: Number(newLot.fineWeight),
                fineWeightToReturn: Number(newLot.fineWeightToReturn),
                furnace: Number(newLot.furnace),
                meltingLoss: Number(newLot.meltingLoss),
                metalReturnPercentage: Number(newLot.metalReturnPercentage),
                netWeight: Number(newLot.netWeight),
                settlementFineness: Number(newLot.settlementFineness),
                wamt: Number(newLot.wamt),
                weightAfterMelting: Number(newLot.weightAfterMelting),
                metalType: newLot.metalType,
                id: lotNumber,
                manualEntryMade: manualInput && !buybackAssuredCase()
            }
            params.lotDetails = lotDetails
            if(lotDetails.wamt === 0){
                params.isDbStore = true
            }
            
            if(lotDetails.customerTakenWeight !== prevData.customerTakenWeight && prevData.finalSettlementNetWeight && newLot.wamt && Number(newLot.wamt) > 0){ 
                params.updateCTW = true
            }
        }
        const { error } = await execute(() => createOrUpdateLotDetails(params))
        if (error === null) {
            notify(lotNumber ? 'Melting Lot Updated.' : 'Melting Lot Created.')
            closeMeltingLot()
        }
    }


    const initializeFormData = () => {
        const currentLines = lineItems.filter(line => lineIds.includes(line.id!))

        let declaredWeight = 0
        let netWeight = 0
        let metalType = currentLines[0]?.metalType || METAL_TYPES.GOLD

        currentLines.forEach(line => {
            declaredWeight += line.declaredWeight
            netWeight += line.netWeight
        })

        const initialFormData: TMeltingLot = {
            declaredWeight, netWeight
        } as TMeltingLot
        
        if(transactionType === TRANSACTION_TYPES.BUY_BACK) initialFormData.metalType = metalType

        setFormData(prev => ({ ...prev, ...initialFormData }))
    }

    const settingUpFields = () => {
        if (!lotNumber) {
            initializeFormData()
            setFormConfig(prev => {

                const createLotFields: (keyof TMeltingLot)[] = ['netWeight', 'declaredWeight']

                const fields: TField<TMeltingLot>[] = [...prev.map(field => {

                    const { validation, ...restField } = field

                    if (!createLotFields.includes(restField.fieldName)) {
                        restField.isVisible = false
                    }

                    if (transactionType === TRANSACTION_TYPES.BUY_BACK && restField.fieldName === 'metalType') {
                        restField.isVisible = true
                        // restField.disabled = false
                    }

                    return restField
                })]

                return fields
            })
        }
        else {
            const lotData = meltingLot.filter(lot => lot.id === lotNumber)[0]
            setFormData(lotData)

            if (buybackAssuredCase()) {
                setManualFinness(true)
            }

            setFormConfig(prev => {

                const fields = [...prev.map(field => {

                    if (field.fieldName === 'fineness') field.RenderField = XRFField

                    switch (transactionType) {
                        case TRANSACTION_TYPES.BUY_BACK:
                            if (doesLotContainAssuredProducts()) {
                                if (field.fieldName === 'fineness') {
                                    delete field.RenderField
                                    field.fieldType = 'text' as TFieldType
                                    field.regexFilter = FLOAT_REGEX
                                    field.decimalPlaces = 1
                                }
                                if (['customerTakenWeight'].includes(field.fieldName)) {
                                    field.disabledManualInput = true
                                    delete field.validation
                                }
                            }

                            if (field.fieldName === 'metalType') {
                                field.isVisible = true
                                // field.disabled = false
                            }
                            break;

                        case TRANSACTION_TYPES.PURITY_TESTING:
                            if (field.fieldName === 'customerTakenWeight') {
                                field.disabled = true;
                                field.validation = field.validation ? { ...field.validation, required: false } : { required: false };
                            }
                            break;

                    }

                    if(lotData.settlementNetWeight && ['weightAfterMelting', 'wamt', 'furnace', 'fineness'].includes(field.fieldName)){
                        field.disabled = true
                    }

                    return field

                })]

                return fields
            })
            
            let metalReturnPercentage = 0
            if(lotData?.metalReturnPercentage) metalReturnPercentage = lotData.metalReturnPercentage
            else {
                switch (transactionType) {
                    case TRANSACTION_TYPES.BUY_SELL:
                        metalReturnPercentage = customerConfig.buysellMetalReturnPercentage!
                        break;
                    case TRANSACTION_TYPES.JOB_WORK:
                        metalReturnPercentage = customerConfig.jobworkMetalReturnPercentage!
                        break;
                    default:
                        metalReturnPercentage = 0
                }
            }
            if(metalReturnPercentage) setFormData(prev => ({ ...prev, metalReturnPercentage }))
        }
    }

    const toggleManualInputFields = () => {

        if(!manualInput && !initialRender) {
            setFormData(prev => ({
                ...prev, 
                customerTakenWeight: '' as unknown as number, 
                weightAfterMelting:'' as unknown as number, 
                wamt: '' as unknown as number
            }))
        }
        
        setFormConfig(prev => prev
            .map(field => {
                switch (field.fieldName) {
                    case 'customerTakenWeight':
                    case 'weightAfterMelting':
                    case 'wamt':
                        field.disabledManualInput = !manualInput && !buybackAssuredCase()
                        break;
                }
                return field
            })
        )

        initialRender && setInitialRender(false)
    }

    useEffect(() => {
        settingUpFields()

        if(status !== TRANSACTION_STATUS.INBOUND_INITIATED){
            setFormConfig((prev:any)=>{ 
                return [...prev.map((field:any)=>({...field , disabled:true}))]
            })
        }
    }, [])

    useEffect(()=>{
        toggleManualInputFields()
    },[manualInput])

    useEffect(()=>{
        meltingLot.forEach(lot => {
            if(lot.manualEntryMade && lot.id === lotNumber) {
                setManualInput(true)
            }
        })
    },[meltingLot])

    return (
        <div style={{ padding: '0 10px' }}>
            <div style={{ borderBottom: '1px solid #d8d8d8' }}>
                <Title style={{ textAlign: 'left', margin: '20px 10px 10px 10px', color: '#7f7d76' }}>Add/Edit Inbound Melting Lot</Title>
            </div>
            <div style={{ padding: '20px 0px' }}>
                {/* <DynamicForm {...registerForm()} onSubmit={!isLotCompleted ? submitHandler : undefined} submitText={lotNumber ? 'Update' : 'Create'} /> */}
                <DynamicForm {...registerForm()} onSubmit={status === TRANSACTION_STATUS.INBOUND_INITIATED? submitHandler : undefined } submitText={lotNumber ? 'Update' : 'Create'} />
            </div>
            <Divider />
            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                {privilages.admin.all && !buybackAssuredCase() && lotNumber ? 
                    (manualInput ? 
                        <Button variant='contained' type='button' color='error' onClick={() => setManualInput(prev => !prev)} >Remove Manual Input</Button> :
                        <Button variant='contained' type='button' color='primary' onClick={() => setManualInput(prev => !prev)} >Enable Manual Input</Button> 
                    ):
                <></>}
                    <Button variant='outlined' type='submit' color='secondary' onClick={closeMeltingLot} >{'Close'}</Button>
                </div>
            </div>
        </div>
    )
}

export default MeltingLot